import React from "react";
import InfiniteLoaderLib from "react-window-infinite-loader";
import { KAvatar, KButton, KButtonProps, KIcon, KList, useSetState } from "@kyburz/kdesign-system";
import { InputAdornment, InputBase, InputLabel } from "@material-ui/core";
import { useStyles } from "./styles";
import { useRemValue } from "../../../hooks/useBaseFontSize";

interface Props {
  label?: string;
  value?: string;
  disabled?: boolean;
  onSelect?: (element: any) => void;
  style?: React.CSSProperties;
  elements?: any;
  placeholder?: string;
  height?: number;
  isClearable?: boolean;
  hasNextPage?: boolean;
  loadNextPage?: any;
  handleQueryChange?: (value: string) => void;
  searchPlaceholder?: string;
  required?: boolean;
  isAddingForm?: boolean;
  addingButtonProps?: KButtonProps;
  onToggleForm?: () => void;
  onSubmitForm?: (e: any) => void;
  AddingFormComponent?: any;
  addingFormTitle?: string;
  hasAvatar?: boolean;
  addingFormProps?: Record<string, any>;
}

const KSelectInfinite = ({
  label = "",
  value = "",
  disabled = false,
  onSelect = () => {},
  style = { width: 240 },
  elements = [],
  placeholder = "Select...",
  height = 275,
  isClearable = true,
  hasNextPage = false,
  loadNextPage = () => {},
  handleQueryChange = undefined,
  searchPlaceholder = "Search",
  required = false,
  isAddingForm = false,
  addingButtonProps = {},
  onSubmitForm = () => {},
  onToggleForm = () => {},
  AddingFormComponent = null,
  addingFormTitle = "",
  hasAvatar = false,
  addingFormProps = {},
}: Props) => {
  const classes = useStyles({ size: 40, disabled });
  const InfiniteLoader =
    InfiniteLoaderLib as unknown as React.ComponentType<any>;

  const [{ listOpen, formOpen }, setState] = useSetState({
    listOpen: false,
    formOpen: false,
  });
  const isQueryActive = Boolean(handleQueryChange);
  const itemCount = hasNextPage ? elements.length + 1 : elements.length;
  const isPageLoaded = (index: number) =>
    !hasNextPage || index < elements?.length;

  const toggleList = () => setState({ listOpen: !listOpen, formOpen: false });
  const toggleForm = () => {
    setState({ formOpen: !formOpen });
    onToggleForm();
  };

  const handleSelect = (index: number) => {
    onSelect(elements[index]);
    toggleList();
  };

  const clearSelect = (e: any) => {
    e.stopPropagation();
    onSelect("");
  };

  const handleSubmitForm = (e: any) => {
    setState({ formOpen: !formOpen });
    onSubmitForm(e);
  };

  const startAdornment = (
    <InputAdornment position="end">
      <KIcon icon="search" size={20} color="black" />
    </InputAdornment>
  );

  const handleInputChange = (e: any) =>
    handleQueryChange && handleQueryChange(e.target.value);

  const heightRem = useRemValue(height);

  let listHeight = height;
  if (isQueryActive) listHeight -= 65;
  if (isAddingForm) listHeight -= 65;
  const listHeightRem = useRemValue(listHeight);

  const elementsComponent = elements.map((x: any) => (
    <div className={classes.elementContainer} key={x.id}>
      {hasAvatar ? (
        <KAvatar
          className={classes.avatar}
          avatar={x.avatar}
          firstName={x.first_name ? x.first_name : ""}
          lastName={x.last_name ? x.last_name : ""}
          size={28}
        />
      ) : null}
      <div className={classes.text}>{x.label}</div>
    </div>
  ));

  return (
    <div style={{ marginTop: "1rem", ...style }} className={classes.root}>
      {label ? (
        <InputLabel required={required} className={classes.title}>
          {label}
        </InputLabel>
      ) : null}
      <div className={classes.wrapperSelect}>
        <div
          className={classes.headerSelect}
          onClick={disabled ? undefined : toggleList}
        >
          <div className={classes.titleContainer}>
            {value === "" ? (
              <p className={classes.noTitleSelect}>{placeholder}</p>
            ) : (
              <p className={classes.titleSelect}>{value}</p>
            )}
          </div>
          {!disabled && (
            <div className={classes.flex}>
              {isClearable && value && (
                <KIcon
                  icon="x"
                  size={16}
                  color="#383F51"
                  onClick={disabled ? undefined : clearSelect}
                />
              )}
              {listOpen ? (
                <KIcon icon="chevron-up" size={20} color="#383F51" />
              ) : (
                <KIcon icon="chevron-down" size={20} color="#383F51" />
              )}
            </div>
          )}
        </div>
        {listOpen && !formOpen && (
          <div
            className={classes.dropDownSelect}
            style={{ maxHeight: heightRem }}
          >
            <div className={classes.dropdownControls}>
              {isQueryActive && (
                <InputBase
                  className={classes.inputBase}
                  placeholder={searchPlaceholder}
                  startAdornment={startAdornment}
                  autoFocus
                  inputProps={{
                    onChange: handleInputChange,
                  }}
                />
              )}
              {isAddingForm && (
                <div className={classes.addingButtonContainer}>
                  <KButton {...addingButtonProps} onClick={toggleForm} />
                </div>
              )}
            </div>
            <InfiniteLoader
              isItemLoaded={isPageLoaded}
              itemCount={itemCount}
              loadMoreItems={loadNextPage}
            >
              {({ onItemsRendered, ref }: any) => (
                <KList
                  elements={elementsComponent}
                  onRowClick={handleSelect}
                  height={listHeightRem}
                  onItemsRendered={onItemsRendered}
                  listRef={ref}
                  width="auto"
                />
              )}
            </InfiniteLoader>
          </div>
        )}
        {listOpen && formOpen && AddingFormComponent && (
          <div
            className={classes.dropDownSelect}
            style={{ maxHeight: heightRem }}
          >
            <div className={classes.addingFormContainer}>
              <div className={classes.addingFormHeader}>
                <span>{addingFormTitle}</span>
                <KIcon icon="x" onClick={toggleForm} />
              </div>
              <AddingFormComponent
                {...addingFormProps}
                onSubmit={handleSubmitForm}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KSelectInfinite;
